import React, { useState } from 'react';
import { MAIN_COUPON_CODE, ALL_COUPONS } from '../constants';
import { ArrowRight, Zap, Trophy, ShieldCheck, Copy, Check, Sparkles, GraduationCap, Heart, Clock, UserCheck, ShieldAlert, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000 perspective-2000">
      {/* Hero Showcase Section */}
      <section className="relative pt-12 pb-16 sm:pt-20 sm:pb-40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-24 items-start">

            <div className="text-left relative z-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-morphism border border-blue-600/30 text-blue-400 text-[8px] sm:text-[10px] font-black mb-6 sm:mb-10 tracking-[0.2em] sm:tracking-[0.4em] uppercase animate-float">
                <Zap size={12} className="animate-pulse" />
                Verified PW Ambassador Portal 2026
              </div>

              <h1 className="text-3xl sm:text-6xl md:text-8xl font-black tracking-tighter mb-4 sm:mb-10 leading-[1.1] sm:leading-[0.85] text-white uppercase italic preserve-3d transform-gpu hover:rotate-x-12 transition-transform duration-1000">
                PHYSICS WALLAH <br />
                <span className="text-blue-600 not-italic relative inline-block">
                  COUPON CODE 2026
                  <Sparkles className="absolute -top-4 -right-4 sm:-top-10 sm:-right-16 text-yellow-400 w-6 h-6 sm:w-12 sm:h-12 animate-orbit" />
                </span>
              </h1>

              <p className="text-sm sm:text-lg md:text-xl text-gray-400 max-w-xl mb-6 sm:mb-8 leading-relaxed font-medium">
                The most authoritative resource for Physics Wallah verified coupons.
                Save on premium batches at revolutionary prices using the official PW referral code <span className="text-blue-500 font-black italic underline decoration-blue-500/20">{MAIN_COUPON_CODE}</span>.
              </p>

              {/* CTA Buttons - Vertical Stack */}
              <div className="flex flex-col gap-4 items-start w-full max-w-md">
                {/* Primary CTA */}
                <Link to="/pw-coupon-code" className="w-full bg-blue-600 text-white px-8 py-4 sm:px-10 sm:py-5 rounded-2xl font-black uppercase text-xs sm:text-sm tracking-[0.1em] sm:tracking-[0.15em] hover:bg-blue-700 transition-all transform hover:-translate-y-1 hover:scale-[1.02] active:scale-95 shadow-[0_20px_40px_rgba(37,99,235,0.4)] flex items-center justify-center gap-3 group" aria-label="Browse All PW Coupons">
                  Explore All PW Discounts <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                </Link>

                {/* Secondary CTA - Exam Categories */}
                <Link to="/pw-exam-categories" className="w-full bg-blue-700 text-white px-8 py-3 sm:px-10 sm:py-4 rounded-xl font-black uppercase text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.12em] hover:bg-blue-600 transition-all transform hover:-translate-y-1 active:scale-95 shadow-[0_12px_30px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 group" aria-label="Discover PW Exam Categories">
                  PW Exam Categories <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>

                {/* Secondary CTA - Blogs */}
                <Link to="/blogs" className="w-full bg-blue-700 text-white px-8 py-3 sm:px-10 sm:py-4 rounded-xl font-black uppercase text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.12em] hover:bg-blue-600 transition-all transform hover:-translate-y-1 active:scale-95 shadow-[0_12px_30px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 group" aria-label="Read PW Preparation Blogs">
                  PW Preparation Blogs <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* 1M+ SAVERS - Verified Users Section */}
              <div className="flex items-center justify-center sm:justify-start mt-8 sm:mt-12">
                <div className="flex items-center gap-4 sm:gap-6 p-4 sm:p-6 glass-morphism rounded-2xl sm:rounded-3xl border border-blue-600/30 shadow-[0_10px_40px_rgba(37,99,235,0.15)]">
                  <div className="flex -space-x-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border-2 border-blue-600/60 bg-[#111] flex items-center justify-center overflow-hidden shadow-lg transform hover:scale-110 transition-transform">
                         <img src={`https://i.pravatar.cc/100?img=${i+40}`} alt="Successful PW Student" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                  <div className="text-left">
                    <p className="text-lg sm:text-2xl font-black text-white uppercase tracking-wider">1M+ SAVERS</p>
                    <p className="text-[9px] sm:text-xs text-blue-400 font-black uppercase tracking-[0.2em]">Verified Users</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3D Visual Centerpiece - Vertical Stack Layout */}
            <div className="flex flex-col gap-6 sm:gap-10 items-center w-full relative perspective(2000px) preserve-3d scale-[0.85] sm:scale-100">
              <div className="absolute inset-0 bg-blue-600/10 blur-[60px] sm:blur-[120px] rounded-full animate-pulse-glow"></div>

              {/* First Coupon Card - Universal Gateway */}
              <div className="relative group transition-all duration-1000 ease-out transform-gpu hover:rotate-y-[-15deg] hover:rotate-x-[5deg] preserve-3d w-full">
                <div className="relative w-full max-w-[320px] sm:max-w-[400px] mx-auto glass-morphism border border-white/10 rounded-[32px] sm:rounded-[50px] p-8 sm:p-12 shadow-[0_40px_80px_rgba(0,0,0,0.6)] overflow-hidden preserve-3d">
                  <div className="absolute inset-0 p-[2px] rounded-[32px] sm:rounded-[50px] overflow-hidden">
                    <div className="absolute inset-[-100%] bg-[conic-gradient(from_0deg,transparent,transparent,transparent,#2563eb)] animate-[spin_8s_linear_infinite]"></div>
                    <div className="absolute inset-[3px] bg-[#0a0a0a] rounded-[29px] sm:rounded-[47px]"></div>
                  </div>

                  <div className="relative z-10 flex flex-col items-center text-center translate-z-40">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-blue-600/20 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-8 border border-blue-600/40 shadow-2xl group-hover:rotate-y-180 transition-transform duration-1000">
                      <GraduationCap className="text-blue-500 w-7 h-7 sm:w-10 sm:h-10" />
                    </div>
                    <div className="mb-3 flex items-center gap-2">
                       <span className="w-2 h-2 bg-green-500 rounded-full animate-ping"></span>
                       <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.3em] text-blue-500">Universal Gateway Open</span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black text-white mb-6 sm:mb-10 tracking-tighter uppercase italic leading-none">PW BATCH<br/>SAVINGS</h2>
                    <div className="w-full bg-black/60 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-10 relative group/code overflow-hidden shadow-2xl">
                      <div className="relative flex items-center justify-between">
                        <span className="text-xl sm:text-3xl font-mono font-black tracking-tighter text-blue-400">{MAIN_COUPON_CODE}</span>
                        <button
                          onClick={() => handleCopy(MAIN_COUPON_CODE)}
                          className={`p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all active:scale-90 shadow-2xl ${copiedCode === MAIN_COUPON_CODE ? 'bg-green-500 text-white' : 'bg-blue-600 text-white shadow-blue-600/40'}`}
                        >
                          {copiedCode === MAIN_COUPON_CODE ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                    <p className="text-[7px] sm:text-[10px] text-gray-700 font-black uppercase tracking-[0.3em]">2026 Physics Wallah Verified</p>
                  </div>
                </div>
              </div>

              {/* Second Coupon Card - New Student */}
              <div className="relative group transition-all duration-1000 ease-out transform-gpu hover:rotate-y-[-15deg] hover:rotate-x-[5deg] preserve-3d w-full">
                <div className="relative w-full max-w-[320px] sm:max-w-[400px] mx-auto glass-morphism border border-white/10 rounded-[32px] sm:rounded-[50px] p-8 sm:p-12 shadow-[0_40px_80px_rgba(0,0,0,0.6)] overflow-hidden preserve-3d">
                  <div className="absolute inset-0 p-[2px] rounded-[32px] sm:rounded-[50px] overflow-hidden">
                    <div className="absolute inset-[-100%] bg-[conic-gradient(from_0deg,transparent,transparent,transparent,#2563eb)] animate-[spin_8s_linear_infinite]"></div>
                    <div className="absolute inset-[3px] bg-[#0a0a0a] rounded-[29px] sm:rounded-[47px]"></div>
                  </div>

                  <div className="relative z-10 flex flex-col items-center text-center translate-z-40">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-blue-600/20 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-8 border border-blue-600/40 shadow-2xl group-hover:rotate-y-180 transition-transform duration-1000">
                      <GraduationCap className="text-blue-500 w-7 h-7 sm:w-10 sm:h-10" />
                    </div>
                    <div className="mb-3 flex items-center gap-2">
                       <span className="w-2 h-2 bg-green-500 rounded-full animate-ping"></span>
                       <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.3em] text-blue-500">Use New Number</span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black text-white mb-6 sm:mb-10 tracking-tighter uppercase italic leading-none">PW NEW<br/>STUDENT</h2>
                    <div className="w-full bg-black/60 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-10 relative group/code overflow-hidden shadow-2xl">
                      <div className="relative flex items-center justify-between">
                        <span className="text-xl sm:text-3xl font-mono font-black tracking-tighter text-blue-400">6296JVYD</span>
                        <button
                          onClick={() => handleCopy('6296JVYD')}
                          className={`p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all active:scale-90 shadow-2xl ${copiedCode === '6296JVYD' ? 'bg-green-500 text-white' : 'bg-blue-600 text-white shadow-blue-600/40'}`}
                        >
                          {copiedCode === '6296JVYD' ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                    <p className="text-[7px] sm:text-[10px] text-gray-700 font-black uppercase tracking-[0.3em]">2026 Physics Wallah Verified</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portal Grid Section - Mobile Optimized */}
      <section className="max-w-6xl mx-auto px-4 mb-20 sm:mb-48">
        <h2 className="text-xl sm:text-4xl font-black text-white uppercase tracking-tighter text-center mb-10 italic">Verified PW Batch Coupons</h2>

        {/* Mobile Card Layout - visible only on small screens */}
        <div className="sm:hidden space-y-4">
          {ALL_COUPONS.map((coupon, idx) => (
            <div key={idx} className="bg-[#111]/80 border border-white/10 rounded-2xl p-5 shadow-lg">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="font-black text-white text-base uppercase tracking-tight italic leading-tight">{coupon.course}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mt-1">2026 Batch</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="bg-black/80 border border-blue-600/40 px-4 py-2.5 rounded-xl font-mono font-black text-blue-400 text-sm flex-shrink-0">
                  {coupon.code}
                </div>
                <button
                  onClick={() => handleCopy(coupon.code)}
                  className={`inline-flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-wider h-10 px-5 rounded-xl transition-all active:scale-90 flex-shrink-0 ${copiedCode === coupon.code ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                >
                  {copiedCode === coupon.code ? <Check size={14} /> : <Copy size={14} />}
                  {copiedCode === coupon.code ? 'COPIED' : 'COPY'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout - hidden on small screens */}
        <div className="hidden sm:block bg-[#111]/60 glass-morphism rounded-[40px] p-1 overflow-hidden border border-white/10 shadow-xl">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/5 border-b border-white/5">
                <th className="px-12 py-8 font-black uppercase text-[11px] tracking-widest text-blue-500">Batch Selection</th>
                <th className="px-12 py-8 font-black uppercase text-[11px] tracking-widest text-gray-500">Coupon</th>
                <th className="px-12 py-8 font-black uppercase text-[11px] tracking-widest text-gray-500 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {ALL_COUPONS.map((coupon, idx) => (
                <tr key={idx} className="hover:bg-blue-600/5 transition-all group">
                  <td className="px-12 py-10">
                    <div className="font-black text-white text-xl uppercase tracking-tight italic">{coupon.course}</div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-gray-700 mt-1">2026 Batch</div>
                  </td>
                  <td className="px-12 py-10">
                    <div className="inline-block bg-black/60 border border-blue-600/30 px-6 py-3 rounded-xl font-mono font-black text-blue-400 text-xl">
                      {coupon.code}
                    </div>
                  </td>
                  <td className="px-12 py-10 text-right">
                    <button
                      onClick={() => handleCopy(coupon.code)}
                      className={`inline-flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest h-14 px-10 rounded-2xl transition-all active:scale-90 ${copiedCode === coupon.code ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                    >
                      {copiedCode === coupon.code ? <Check size={14} /> : <Copy size={14} />}
                      {copiedCode === coupon.code ? 'Applied' : 'Get Discount'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Authority Content: Why Choose Us */}
      <section className="max-w-7xl mx-auto px-4 mb-20 sm:mb-48">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-16 items-stretch">
           <div className="order-2 md:order-1 flex">
              <div className="bg-white/5 border border-white/10 rounded-[30px] sm:rounded-[40px] p-8 sm:p-12 relative overflow-hidden group flex flex-col h-full w-full">
                 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-transparent"></div>
                 <h2 className="text-2xl sm:text-4xl font-black text-white uppercase tracking-tighter italic mb-6 sm:mb-8">How to Use PW Coupon Code JITJAN0001?</h2>
                 <p className="text-sm sm:text-base text-gray-400 font-medium leading-relaxed mb-6 sm:mb-8">
                    Physics Wallah has revolutionized education in India. As student ambassadors, we ensure you never pay more than necessary. Our system verifies the <span className="text-blue-500 font-black">PW Referral Code</span> every 24 hours to ensure it provides the maximum possible rebate on JEE, NEET, and GATE batches.
                 </p>
                 <div className="space-y-4 mt-auto">
                    <div className="flex items-center gap-3 sm:gap-4 text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-500 group-hover:text-white transition-colors">
                       <ShieldCheck className="text-green-500 shrink-0" size={16} /> Syncs with PW servers for 2026 session
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-500 group-hover:text-white transition-colors">
                       <UserCheck className="text-blue-500 shrink-0" size={16} /> Verified by Lead Ambassador Jitesh Jana
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 text-[9px] sm:text-xs font-black uppercase tracking-widest text-gray-500 group-hover:text-white transition-colors">
                       <Clock className="text-yellow-500 shrink-0" size={16} /> Instant activation on PW app
                    </div>
                 </div>
              </div>
           </div>
           <div className="order-1 md:order-2 flex">
              <div className="min-h-[300px] md:min-h-0 w-full bg-[#111] rounded-[30px] sm:rounded-[60px] overflow-hidden border border-white/10 relative group">
                 <img src="https://i.postimg.cc/GhHX9R4p/Online-study-analytical.png" alt="Physics Wallah Aspirant" className="absolute inset-0 w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-60 group-hover:scale-110 transition-all duration-1000" loading="lazy" />
                 <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent"></div>
                 <div className="absolute bottom-6 left-6 sm:bottom-12 sm:left-12">
                    <h3 className="text-xl sm:text-4xl font-black text-white uppercase tracking-tighter italic leading-none">JEE & NEET<br/><span className="text-blue-500">SAVINGS HUB</span></h3>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Goal Showcase */}
      <section className="max-w-7xl mx-auto px-4 mb-20 sm:mb-48 relative">
        <div className="text-center mb-10 sm:mb-24">
           <h2 className="text-gray-100 text-lg sm:text-2xl font-black uppercase tracking-[0.1em] sm:tracking-[0.2em] mb-4">Browse PW Exam Categories</h2>
           <div className="w-20 sm:w-40 h-1 bg-blue-600 mx-auto rounded-full shadow-[0_0_20px_rgba(37,99,235,0.5)]"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-12">
          {[
            { title: 'PW JEE Referral', icon: <Trophy />, path: '/pw-jee-coupon-code', desc: 'Arjuna & Lakshya JEE' },
            { title: 'PW NEET Referral', icon: <Heart />, path: '/pw-neet-coupon-code', desc: 'Yakeen & Lakshya NEET' },
            { title: 'PW GATE Referral', icon: <ShieldCheck />, path: '/pw-gate-coupon-code', desc: 'Parakram & Vijay GATE' }
          ].map((cat, i) => (
            <Link
              key={i}
              to={cat.path}
              className="card-3d p-8 sm:p-16 rounded-[24px] sm:rounded-[60px] glass-morphism hover:bg-blue-600/10 hover:border-blue-500/40 group flex flex-col items-center text-center shadow-xl overflow-hidden relative"
            >
              <div className="mb-6 sm:mb-10 p-5 sm:p-8 bg-blue-600/10 rounded-2xl sm:rounded-[40px] group-hover:scale-110 transition-all text-blue-500 shadow-lg border border-blue-600/20">
                {React.cloneElement(cat.icon as any, { size: 32 })}
              </div>
              <h3 className="text-lg sm:text-2xl font-black mb-2 text-white uppercase tracking-tighter italic">{cat.title}</h3>
              <p className="text-gray-600 text-[8px] sm:text-[10px] mb-6 font-black uppercase tracking-widest">{cat.desc}</p>
              <span className="flex items-center gap-2 text-blue-500 text-[9px] sm:text-[11px] font-black uppercase tracking-[0.2em] group-hover:text-white">
                Enter Discount Portal <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Independent Hub Disclaimer */}
      <section className="max-w-7xl mx-auto px-4 mb-20 sm:mb-32">
        <div className="bg-[#111] border border-white/5 rounded-[24px] sm:rounded-[40px] p-8 sm:p-12 relative overflow-hidden group">
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-12 text-center md:text-left">
            <div className="bg-white/5 p-4 rounded-xl border border-white/10 shrink-0">
               <ShieldAlert className="text-gray-600" size={24} />
            </div>
            <div className="flex-grow">
              <h4 className="text-base sm:text-lg font-black text-white uppercase tracking-tighter mb-2 italic">Independent Ambassador Disclosure</h4>
              <p className="text-gray-600 text-[9px] sm:text-[11px] font-bold leading-relaxed mb-6 uppercase">
                EduOffers is an independent community resource managed by Lead Ambassador Jitesh Jana. We are officially recognized student ambassadors for Physics Wallah, but we are not the official website of Physics Wallah Private Limited.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <Link to="/legal-disclaimer" className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-blue-500 flex items-center gap-1">
                  Compliance Report <ArrowUpRight size={10} />
                </Link>
                <Link to="/safety-and-transparency" className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-white">
                  Trust Pillars
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;